import type {
  ArtSeriesApiSortingEnum,
  IApiResponse,
  ArtDetailSerializer,
  FacetedArtsDataSerializer,
  FacetedArtsResponseSerializer,
  SeriesFullSerializer,
  FullSeriesPricesSerializer,
} from "@litres-libs/types";
import { rtkqApi } from "../api/rtkqApi";

import type { IArtsFacetsRequest } from "../../types/api";
import { TagsType } from "../constants";

interface ISeriesRequest {
  series_id: number;
  art_groups?: number[];
  o?: "new" | "popular" | "order_in_series_desc";
  limit?: number;
  after?: string;
  before?: string;
}

interface ISeriesRequestById {
  artId: number;
  art_groups?: number[];
  o?: "new" | "popular" | "order_in_series_desc";
  limit?: number;
  after?: string;
  before?: string;
}

export interface ISeriesArtsFacetsRequest extends IArtsFacetsRequest {
  seriesIdOrSlug: string | number;
  o?: ArtSeriesApiSortingEnum;
}

export const seriesApi = rtkqApi.injectEndpoints({
  endpoints: build => ({
    getSeriesArts: build.query<
      IApiResponse<ArtDetailSerializer[]>["payload"],
      ISeriesRequest
    >({
      query: ({ series_id, limit = 12, ...params }) => {
        return {
          url: `/foundation/api/series/${series_id}/arts`,
          params: {
            limit,
            ...params,
          },
        };
      },
      transformResponse: (response: IApiResponse<ArtDetailSerializer[]>) => {
        return response.payload;
      },
      providesTags: result =>
        result
          ? [
              ...result.data.map(({ id }) => ({
                type: TagsType.ART_SHORT_LIST as const,
                id,
              })),
              TagsType.ART_SHORT_LIST,
            ]
          : [TagsType.ART_SHORT_LIST],
    }),
    getSeriesArtsByArtId: build.query<
      IApiResponse<ArtDetailSerializer[]>["payload"],
      ISeriesRequestById
    >({
      query: ({ artId, limit = 12, ...params }) => {
        return {
          url: `/foundation/api/arts/${artId}/series/arts`,
          params: {
            limit,
            ...params,
          },
        };
      },
      transformResponse: (response: IApiResponse<ArtDetailSerializer[]>) => {
        return response.payload;
      },
      providesTags: result =>
        result
          ? [
              ...result.data.map(({ id }) => ({
                type: TagsType.ART_SHORT_LIST as const,
                id,
              })),
              TagsType.ART_SHORT_LIST,
            ]
          : [TagsType.ART_SHORT_LIST],
    }),
    /** запрос на фасеты для новой сетки, мерж  не нужен
     */
    getSeriesFacets: build.query<
      FacetedArtsDataSerializer,
      ISeriesArtsFacetsRequest
    >({
      query: ({ seriesIdOrSlug, limit = 24, offset = "0", ...params }) => ({
        url: `/foundation/api/series/${seriesIdOrSlug}/arts/facets`,
        params: {
          ...params,
          limit,
          offset,
        },
        headers: {
          "accept-version": "2",
        },
      }),
      transformResponse: (response: FacetedArtsResponseSerializer) =>
        response.payload,
      providesTags: result =>
        result
          ? [
              ...result.data.map(({ id }) => ({
                type: TagsType.ART_SHORT_LIST as const,
                id,
              })),
              TagsType.ART_SHORT_LIST,
            ]
          : [TagsType.ART_SHORT_LIST],
    }),
    getSeriesMetaInfo: build.query<
      SeriesFullSerializer,
      { seriesIdOrSlug: string | number }
    >({
      query: ({ seriesIdOrSlug }) => ({
        url: `/foundation/api/series/${seriesIdOrSlug}`,
      }),
      transformResponse: (response: IApiResponse<SeriesFullSerializer>) =>
        response.payload.data,
    }),
    getSeriesPrices: build.query<
      FullSeriesPricesSerializer,
      { seriesIdOrSlug: string | number }
    >({
      query: ({ seriesIdOrSlug }) => ({
        url: `/foundation/api/series/${seriesIdOrSlug}/price`,
      }),
      transformResponse: (response: IApiResponse<FullSeriesPricesSerializer>) =>
        response.payload.data,
    }),
  }),
});
